import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { Backdrop, Paper, Skeleton } from "@mui/material";
import { getFormattedAmount } from "../../utils/AmountUtils";
import { ToggleWalletActions, WalletAction, WalletActionsModal } from "./walletStyles";
import { fetchAdminWallet, getProfileCompletion } from "../../screens/dashboard/DashboardUtils";
import {
	saveWalletInfo,
	setLoadingState,
	toggleActivateWalletModal,
	toggleAddFundsModal,
	toggleWithdrawFundsModal,
} from "../../redux/WalletSlice";
import DepositFlow from "./DepositFlow";
import WithdrawalFlow from "./WithdrawalFlow";
import InactiveWalletIcon from "../../assets/icons/inactive-wallet-icon.svg";
import ActivateWallet from "./walletusables/ActivateWallet";
import { getCurrentUserInfo } from "../../utils/UserAuth";

const BalanceCard = ({ wallet }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const currentAdmin = getCurrentUserInfo();
	const [amountDisplayed, setAmountDisplayed] = useState(true);
	const [toggleModal, setToggleModal] = useState(false);
	const { loading, addFundsModal, withdrawFundsModal, activateWalletModal } = useSelector(
		(state) => state.wallet
	);
	const [activeDepositTab, setActiveDepositTab] = useState(1);
	const [activeWithdrawalTab, setActiveWithdrawalTab] = useState(1);
	const [activeWalletTab, setActiveWalletTab] = useState(1);

	// REMOVE SCROLL ON MODAL OPEN
	useEffect(() => {
		if (toggleModal || addFundsModal || withdrawFundsModal || activateWalletModal) {
			document.body.style.overflow = "hidden";
		}
		return () => {
			// eslint-disable-next-line no-undef
			document.body.style.overflow = "auto";
		};
	}, [toggleModal, addFundsModal, withdrawFundsModal, activateWalletModal]);

	// GET TOKEN BASED ON PATHNAME
	const adminToken = location.pathname.includes("logistics")
		? process.env.REACT_APP_LOGISTICS_ADMIN_WALLET_ID
		: process.env.REACT_APP_ADMIN_WALLET_ID;

	// FUNCTION TO FETCH ADMIN WALLET
	const fetchWallet = async () => {
		dispatch(setLoadingState(true));
		try {
			const res = await fetchAdminWallet(adminToken);
			const data = { wallet: res?.result?.accounts, otherWalletDetails: res?.result };
			dispatch(saveWalletInfo(data));
		} catch (error) {
			if (error === "This session has expired. Please Login again") {
				navigate("/login");
			} else {
				toast.error(error);
			}
		} finally {
			dispatch(setLoadingState(false));
		}
	};

	const onBeginAddFunds = () => {
		setToggleModal(false);
		setActiveDepositTab(1);
		dispatch(toggleAddFundsModal(true));
	};

	const onBeginWithdrawFunds = () => {
		setToggleModal(false);
		setActiveWithdrawalTab(1);
		dispatch(toggleWithdrawFundsModal(true));
	};

	const onBeginActivateWallet = () => {
		dispatch(toggleActivateWalletModal(true));
		setActiveWalletTab(1);
	};

	const walletCriteria = {
		bvn: currentAdmin?.bvn,
		accessPin: currentAdmin?.accessPin,
	};

	const isWalletActive = getProfileCompletion(walletCriteria, 1);

	// fetch admin wallet ON LOAD
	useEffect(() => {
		fetchWallet();
	}, [location.pathname]);

	return (
		<>
			<Backdrop onClick={() => setToggleModal(false)} open={toggleModal} sx={{ zIndex: 13 }}></Backdrop>
			<Backdrop open={addFundsModal} sx={{ zIndex: 13 }}>
				<DepositFlow activeDepositTab={activeDepositTab} setActiveDepositTab={setActiveDepositTab} />
			</Backdrop>
			<Backdrop open={withdrawFundsModal} sx={{ zIndex: 13 }}>
				<WithdrawalFlow
					activeWithdrawalTab={activeWithdrawalTab}
					setActiveWithdrawalTab={setActiveWithdrawalTab}
				/>
			</Backdrop>
			<Backdrop open={activateWalletModal} sx={{ zIndex: 13 }}>
				<ActivateWallet activeTab={activeWalletTab} setActiveTab={setActiveWalletTab} />
			</Backdrop>

			<Paper
				sx={{ width: "325px", px: 2, pt: 2, pb: isWalletActive < 2 ? 0.5 : 4, mb: 5, borderRadius: "8px" }}>
				<div className='flex space-between align-start'>
					<div className='flex gap-2'>
						<p style={{ color: isWalletActive < 2 ? "#AABDC5" : "#8E9DA490" }}>Total Balance</p>
						<button className='input_icon' onClick={() => setAmountDisplayed(!amountDisplayed)} type='button'>
							{!amountDisplayed ? (
								<FaRegEye size={20} color={isWalletActive < 2 ? "#AABDC5" : "#00525D"} />
							) : (
								<FaRegEyeSlash size={20} color={isWalletActive < 2 ? "#AABDC5" : "#00525D"} />
							)}
						</button>
					</div>
					<ToggleWalletActions>
						<button onClick={() => setToggleModal(true)} disabled={isWalletActive < 2 || loading}>
							<BsThreeDotsVertical size={16} color={isWalletActive < 2 ? "#AABDC5" : "#00525D"} />
						</button>

						{toggleModal && (
							<WalletActionsModal>
								<WalletAction onClick={onBeginAddFunds}>Add Funds</WalletAction>
								<WalletAction>View Statements</WalletAction>
								<WalletAction onClick={onBeginWithdrawFunds}>Withdraw</WalletAction>
								{/* <WalletAction

								// onClick={onBeginMoveFunds}
								>
									Move Funds
								</WalletAction> */}
							</WalletActionsModal>
						)}
					</ToggleWalletActions>
				</div>

				{amountDisplayed ? (
					<>
						<div
							className='flex gap-1 items-center customfont'
							style={{ color: isWalletActive < 2 ? "#AABDC5" : "#003A50" }}>
							{loading && wallet ? (
								<p className=''>{getFormattedAmount.format(wallet?.availableBalance || 0)}</p>
							) : loading ? (
								<Skeleton width={110} height={45} />
							) : wallet ? (
								<p className=''>{getFormattedAmount.format(wallet?.availableBalance || 0)}</p>
							) : (
								<p className=''>NGN 00.00</p>
							)}
						</div>
					</>
				) : (
					<p className='mt-5 customfont'>****</p>
				)}
				{isWalletActive < 2 && (
					<div className='space-between' style={{ paddingTop: "1rem" }}>
						<div className='flex' style={{ gap: "4px" }}>
							<img src={InactiveWalletIcon} style={{ width: "20px", height: "20px" }} />
							<p style={{ fontSize: "12px", color: "#555F63" }}>Account is inactive</p>
						</div>
						<button
							style={{
								fontWeight: "600",
								fontSize: "13px",
								border: "1px solid #BCD2D5",
								background: "#D3E5E780",
								borderRadius: "16px",
								padding: "6px 16px",
								color: "#00525D",
							}}
							onClick={onBeginActivateWallet}>
							Activate Account
						</button>
					</div>
				)}
			</Paper>
		</>
	);
};

export default BalanceCard;

BalanceCard.propTypes = {
	wallet: PropTypes.object,
};
